/**
 * Top Header (STEP 5) — hospital/branch/workspace switchers, quick
 * search, notifications, user menu. Everything here is real, wired
 * client state (Zustand) — the switchers themselves list only what
 * `store/hospital.store.ts`/`store/branch.store.ts` currently hold
 * (no HMIS module exists yet to populate them from a real `GET
 * /hospitals`/`GET /branches` call — that wiring is Stage F1+'s own
 * job, once a real page needs it).
 */
import { useState, type JSX, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, IconButton, Typography, Box, Menu, MenuItem, Avatar, Divider, ListItemIcon, Tooltip, Chip, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import { useLayoutStore } from '@store/layout.store';
import { useThemeStore } from '@store/theme.store';
import { useAuth } from '@hooks/useAuth';
import { NotificationCenter } from '@components/navigation/NotificationCenter';
import { TextSizeControl } from './TextSizeControl';
import { useCurrentFinancialYear } from '@features/administration/hooks/useFinancialYear';
import { useCorrectionWorkflowMe } from '@features/billing/hooks/useCorrectionWorkflow';
import { useCanOverseeCorrections, useCanRequestCorrection } from '@hooks/usePermission';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { HospitalBranchSwitcher } from './HospitalBranchSwitcher';

const HEADER_HEIGHT = 48;

export function Header(): JSX.Element {
  const toggleSidebar = useLayoutStore((state) => state.toggleSidebar);
  const toggleMobileSidebar = useLayoutStore((state) => state.toggleMobileSidebar);
  const setQuickSearchOpen = useLayoutStore((state) => state.setQuickSearchOpen);
  const mode = useThemeStore((state) => state.mode);
  const setMode = useThemeStore((state) => state.setMode);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const { data: financialYear } = useCurrentFinancialYear();
  // The corrections link (desk, 2026-10-05): shown to someone selected as a verifier, and to anyone who can enter a request (and, since 2026-10-18, to the administrator who may see the register). Its count is
  // what waits for them to VERIFY. Approving is in Modifications (desk, 2026-10-07), which has its own link below — shown to selected
  // approvers only, so neither link counts the other stage's work.
  const correctionWorkflow = useCorrectionWorkflowMe();
  const canRequestCorrection = useCanRequestCorrection();
  // An administrator who holds the approval permission also gets the link: the page behind it shows them the whole register, read-only (owner, 2026-10-18).
  const canOverseeCorrections = useCanOverseeCorrections();
  const selectedForCorrections = Boolean(correctionWorkflow.data?.isVerifier);
  const correctionsWaiting = correctionWorkflow.data?.awaitingMyVerification ?? 0;
  const isApprover = Boolean(correctionWorkflow.data?.isApprover);
  const approvalsWaiting = correctionWorkflow.data?.awaitingMyApproval ?? 0;
  const theme = useTheme();
  // Below `sm`, Sidebar.tsx renders a temporary overlay drawer of its own
  // (`mobileSidebarOpen`) rather than the permanent icon rail this button
  // otherwise toggles — same breakpoint Sidebar.tsx itself checks.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initials = user ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase() : '';

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{ height: HEADER_HEIGHT, justifyContent: 'center', borderBottom: 1, borderColor: 'divider', zIndex: (theme) => theme.zIndex.drawer + 1 }}
    >
      <Toolbar variant="dense" sx={{ minHeight: HEADER_HEIGHT, gap: { xs: 0.25, md: 1 }, px: { xs: 1, md: 2 } }}>
        <IconButton edge="start" aria-label="Toggle navigation" size="small" onClick={isMobile ? toggleMobileSidebar : toggleSidebar}>
          <MenuIcon />
        </IconButton>

        {/* Tighter below `md` (a phone, or a phone turned sideways, ~844px): with the full title, gaps and padding the avatar ended up 28px past the right edge of a 390px screen. */}
        <Typography variant="h6" component="div" sx={{ mr: { xs: 0.5, md: 2 }, fontSize: { xs: '1rem', md: '1.25rem' }, whiteSpace: 'nowrap' }}>
          NEXAO HMIS
        </Typography>

        {/* Hidden below `sm` — real horizontal overflow at phone widths otherwise (confirmed live,
            Stage RC1); a phone user can still switch branch via Quick Actions/Global Search. */}
        <Box display={{ xs: 'none', sm: 'flex' }} gap={1} alignItems="center">
          <HospitalBranchSwitcher />
          <WorkspaceSwitcher />
          {financialYear && (
            <Tooltip title="Current financial year">
              <Chip size="small" variant="outlined" label={`FY ${financialYear.label}`} />
            </Tooltip>
          )}
          {/* The two correction links below show from `lg` (1200px) up: between a phone and a desktop the header has no room for them (a phone turned sideways pushed the bell, text size and avatar off the screen), and the sidebar row carries the same count. */}
          {(selectedForCorrections || canRequestCorrection || canOverseeCorrections) && (
            <Tooltip title={correctionsWaiting > 0 ? `${correctionsWaiting} correction request${correctionsWaiting === 1 ? '' : 's'} waiting for you` : canOverseeCorrections && !selectedForCorrections && !canRequestCorrection ? 'All correction requests and their status' : 'Receipt corrections'}>
              <Chip
                size="small"
                variant={correctionsWaiting > 0 ? 'filled' : 'outlined'}
                color={correctionsWaiting > 0 ? 'warning' : 'default'}
                clickable
                sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
                label={correctionsWaiting > 0 ? `Corrections (${correctionsWaiting})` : 'Corrections'}
                onClick={() => navigate('/corrections')}
              />
            </Tooltip>
          )}
          {isApprover && (
            <Tooltip title={approvalsWaiting > 0 ? `${approvalsWaiting} correction${approvalsWaiting === 1 ? '' : 's'} waiting for your approval` : 'Modifications'}>
              <Chip
                size="small"
                variant={approvalsWaiting > 0 ? 'filled' : 'outlined'}
                color={approvalsWaiting > 0 ? 'warning' : 'default'}
                clickable
                sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
                label={approvalsWaiting > 0 ? `Modifications (${approvalsWaiting})` : 'Modifications'}
                onClick={() => navigate('/modifications/correction')}
              />
            </Tooltip>
          )}
        </Box>

        <Box flexGrow={1} />

        <Tooltip title="Quick search (Ctrl+K)">
          <IconButton aria-label="Quick search" size="small" onClick={() => setQuickSearchOpen(true)}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Not on a phone: the header has no room for it there (the avatar was clipped on a 360px screen) and a phone follows its own dark-mode setting. */}
        <Tooltip title="Toggle color mode">
          <IconButton sx={{ display: { xs: 'none', sm: 'inline-flex' } }} aria-label="Toggle color mode" size="small" onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}>
            <Brightness4Icon fontSize="small" />
          </IconButton>
        </Tooltip>

        <NotificationCenter />

        <TextSizeControl />

        <Tooltip title={user ? `${user.firstName} ${user.lastName}` : 'Account'}>
          <IconButton aria-label="Account menu" size="small" onClick={(event: MouseEvent<HTMLElement>) => setUserMenuAnchor(event.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8125rem' }}>{initials || <PersonIcon fontSize="small" />}</Avatar>
          </IconButton>
        </Tooltip>
        <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
          <Box px={2} py={1}>
            <Typography variant="body2" fontWeight={600}>
              {user ? `${user.firstName} ${user.lastName}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              navigate('/profile');
            }}
          >
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            My Profile
          </MenuItem>
          <Divider />
          <MenuItem onClick={logout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Log out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export { HEADER_HEIGHT };
